// Centralized debug logging utility for OverlayQA extension
// Usage: import { createDebug } from '../utils/debug';
//        const debug = createDebug('ComponentName');
//        debug.log('message', data);
//
// Enable logging: localStorage.setItem('DEBUG_OVERLAYQA', '1')
// Disable logging: localStorage.removeItem('DEBUG_OVERLAYQA')

/** Cached debug flag — avoids hitting localStorage on every call */
let _debugEnabled: boolean | null = null;

/** Check if debug logging is enabled via localStorage flag */
export function isDebugEnabled(): boolean {
  if (_debugEnabled === null) {
    try {
      _debugEnabled = typeof localStorage !== 'undefined'
        && localStorage.getItem('DEBUG_OVERLAYQA') === '1';
    } catch {
      _debugEnabled = false;
    }
  }
  return _debugEnabled;
}

/** Reset the cached debug flag — call after direct localStorage changes (used in tests) */
export function _resetDebugCache(): void {
  _debugEnabled = null;
}

// Listen for storage changes so toggling debug in another tab takes effect
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'DEBUG_OVERLAYQA') {
      _debugEnabled = e.newValue === '1';
    }
  });
}

/**
 * Create a namespaced debug logger for a component/module.
 * - log() and warn() only output when DEBUG_OVERLAYQA=1
 * - error() ALWAYS outputs (real errors should never be silent)
 */
export function createDebug(component: string) {
  const prefix = `[OverlayQA ${component}]`;
  return {
    log: (...args: unknown[]) => { if (isDebugEnabled()) console.log(prefix, ...args); },
    warn: (...args: unknown[]) => { if (isDebugEnabled()) console.warn(prefix, ...args); },
    error: (...args: unknown[]) => { console.error(prefix, ...args); },
  };
}

// --- Legacy API (kept for backward compat during migration) ---

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  component: string;
  message: string;
  data?: unknown;
}

class OverlayQALogger {
  private logs: LogEntry[] = [];
  private maxLogs = 100;

  private addLog(level: LogLevel, component: string, message: string, data?: unknown) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      component,
      message,
      data
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    if (isDebugEnabled()) {
      const prefix = `[OverlayQA ${component}]`;
      const logData = data ? [message, data] : [message];
      switch (level) {
        case 'error':
          console.error(prefix, ...logData);
          break;
        case 'warn':
          console.warn(prefix, ...logData);
          break;
        case 'debug':
          console.debug(prefix, ...logData);
          break;
        default:
          console.log(prefix, ...logData);
      }
    }
  }

  info(component: string, message: string, data?: unknown) { this.addLog('info', component, message, data); }
  warn(component: string, message: string, data?: unknown) { this.addLog('warn', component, message, data); }
  error(component: string, message: string, data?: unknown) { this.addLog('error', component, message, data); }
  debug(component: string, message: string, data?: unknown) { this.addLog('debug', component, message, data); }

  getAllLogs(): LogEntry[] { return [...this.logs]; }
  getComponentLogs(component: string): LogEntry[] { return this.logs.filter(log => log.component === component); }
  clear() { this.logs = []; }
  exportLogs(): string { return JSON.stringify(this.logs, null, 2); }

  persistentPopup = {
    toggleState: (enabled: boolean, context?: unknown) => {
      this.info('PersistentPopup', `Toggling persistent mode: ${enabled}`, context);
    },
    messageReceived: (messageType: string, payload?: unknown) => {
      this.debug('PersistentPopup', `Message received: ${messageType}`, payload);
    },
    messageSent: (messageType: string, payload?: unknown) => {
      this.debug('PersistentPopup', `Message sent: ${messageType}`, payload);
    },
    stateSync: (syncType: 'to-persistent' | 'to-browser', data?: unknown) => {
      this.info('PersistentPopup', `State sync ${syncType}`, data);
    },
    positionChange: (newPosition: { x: number; y: number }) => {
      this.debug('PersistentPopup', 'Position changed', newPosition);
    },
    dragEvent: (eventType: 'start' | 'move' | 'end', position?: unknown) => {
      this.debug('PersistentPopup', `Drag ${eventType}`, position);
    },
    error: (operation: string, error: unknown) => {
      this.error('PersistentPopup', `Error in ${operation}`, error);
    }
  };
}

export const logger = new OverlayQALogger();

export function debugLog(component: string, message: string, data?: unknown) {
  logger.info(component, message, data);
}

// Global window helper for console debugging
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).overlayQADebug = {
    logger,
    enable: () => { localStorage.setItem('DEBUG_OVERLAYQA', '1'); _debugEnabled = true; },
    disable: () => { localStorage.removeItem('DEBUG_OVERLAYQA'); _debugEnabled = false; },
    getLogs: () => logger.getAllLogs(),
    exportLogs: () => logger.exportLogs(),
    clearLogs: () => logger.clear(),
    getComponentLogs: (component: string) => logger.getComponentLogs(component)
  };
}
